import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { DominoTile } from "@/components/DominoTile";
import { ProductImage } from "@/components/ProductImage";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";

const jost = { fontFamily: "Jost, sans-serif" };
const GALLERY = ["duo-editorial", "flacon-macro", "etui-face", "plan-serre"];

const T = {
  fr: {
    notFound: "Référence introuvable.", back: "Retour", backTop: "← Domino",
    serieLabel: (s) => (s === "noire" ? "Série Noire" : "Série Blanche"),
    seoTitle: (p, s) => `DOMINO ${p.roman} — Série ${s} · Maison Partita`,
    seoDesc: (p, s, amb) => `DOMINO ${p.roman}, série ${s}. ${amb}. Eau de parfum concentrée, 50 ml, série limitée numérotée.`,
    tile: (pips) => `Tuile à ${pips[0]} et ${pips[1]} points`,
    altDuo: (REF, sl, pips) => `Étui et flacon ${REF}, ${sl}, tuile à ${pips[0]} points et ${pips[1]} points`,
    altTile: (REF, pips) => `Tuile ${REF}, ${pips[0]} points et ${pips[1]} points`,
    altGallery: { "flacon-macro": "macro du flacon", "etui-face": "étui de face", "plan-serre": "plan large", "duo-editorial": "vue éditoriale" },
    format: "Eau de parfum · 50 ml · Série limitée numérotée",
    perfumer: "Parfumeur : non communiqué.",
    mirror: "Ce numéro existe aussi de l'autre côté du jeu →",
    rrp: "Prix de vente conseillé", enquire: "Nous écrire", soldOut: "Épuisé",
    notify: "Être averti du retour", emailPlaceholder: "Ton adresse e-mail", notifyBtn: "M'avertir",
  },
  en: {
    notFound: "Reference not found.", back: "Back", backTop: "← Domino",
    serieLabel: (s) => (s === "noire" ? "Black Series" : "White Series"),
    seoTitle: (p, s) => `DOMINO ${p.roman} — ${s === "noire" ? "Black" : "White"} Series · Maison Partita`,
    seoDesc: (p, s, amb) => `DOMINO ${p.roman}, ${s === "noire" ? "Black" : "White"} Series. ${amb}. Concentrated eau de parfum, 50 ml, numbered limited series.`,
    tile: (pips) => `Tile with ${pips[0]} and ${pips[1]} pips`,
    altDuo: (REF, sl, pips) => `${REF} case and bottle, ${sl}, tile with ${pips[0]} and ${pips[1]} pips`,
    altTile: (REF, pips) => `${REF} tile, ${pips[0]} and ${pips[1]} pips`,
    altGallery: { "flacon-macro": "bottle macro", "etui-face": "case front", "plan-serre": "wide shot", "duo-editorial": "editorial view" },
    format: "Eau de parfum · 50 ml · Numbered limited series",
    perfumer: "Perfumer: undisclosed.",
    mirror: "This number also exists on the other side of the game →",
    rrp: "Recommended retail price", enquire: "Write to us", soldOut: "Sold out",
    notify: "Be notified of its return", emailPlaceholder: "Your e-mail address", notifyBtn: "Notify me",
  },
};

const NotifyForm = ({ productRef, t }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/notify", { email, ref: productRef });
      toast.success(data.message);
      setEmail("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-8 max-w-md" data-testid="notify-form" style={jost}>
      <p className="text-[13px] tracking-[0.24em] uppercase text-black/60">{t.notify}</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t.emailPlaceholder} className="val-input mt-4" data-testid="notify-email" />
      <button type="submit" disabled={busy} className="val-btn mt-6" data-testid="notify-submit">
        {busy ? "…" : t.notifyBtn}
      </button>
    </form>
  );
};

export default function DominoProduct() {
  const { serie, roman } = useParams();
  const lang = useLang();
  const t = T[lang];
  const serieName = serie === "serie-noire" ? "noire" : "blanche";
  const other = serieName === "noire" ? "blanche" : "noire";
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null); setErr(false);
    api.get(`/products/domino-${serieName}-${roman}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [serieName, roman]);

  const ambiance = p && loc(p, lang, "ambiance");

  useSeo(
    p ? t.seoTitle(p, serieName) : "Domino · Maison Partita",
    p ? t.seoDesc(p, serieName, ambiance) : ""
  );

  useEffect(() => {
    if (!p) return () => {};
    const ld = {
      "@context": "https://schema.org", "@type": "Product",
      name: `DOMINO ${p.roman} — Série ${serieName}`, description: p.ambiance,
      brand: { "@type": "Brand", name: "Domino" },
      offers: { "@type": "Offer", priceCurrency: "EUR", price: p.price,
        availability: p.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, [p, serieName]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-40 text-center text-black/60" style={jost}>{t.notFound} <Link to={lp(lang, "/domino")} className="val-link underline">{t.back}</Link></div>;
  if (!p) return <div className="py-40 text-center text-black/40 tracking-[0.2em] uppercase text-[13px]" style={jost}>…</div>;

  const REF = `DOMINO ${p.roman}`;
  const serieLabel = t.serieLabel(serieName);

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-16 pb-24" style={jost}>
      <Link to={lp(lang, "/domino")} className="val-link text-[13px] tracking-[0.2em] uppercase" data-testid="back-link">{t.backTop}</Link>

      <div className="mt-12 grid gap-16 lg:grid-cols-2">
        <div className="space-y-8">
          <Reveal>
            <div className="overflow-hidden border border-black/10" style={{ background: "#efece3" }}>
              <ProductImage maison="domino" reference={p.reference} suffix="duo" eager
                alt={t.altDuo(REF, serieLabel.toLowerCase(), p.pips)} />
            </div>
          </Reveal>
          {p.available && (
            <div className="grid grid-cols-2 gap-8">
              {GALLERY.map((s, i) => (
                <Reveal key={s} delay={0.1 + i * 0.08}>
                  <div className={`overflow-hidden border border-black/10 ${s === "plan-serre" ? "col-span-2" : ""}`} style={{ background: "#efece3" }}>
                    <ProductImage maison="domino" reference={p.reference} suffix={s}
                      alt={`${REF}, ${serieLabel.toLowerCase()} — ${t.altGallery[s]}`} />
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>

        <div className="lg:pt-6">
          <Reveal>
            <div className="flex items-center gap-8">
              <DominoTile pips={p.pips} variant={serieName} size={82}
                alt={t.altTile(REF, p.pips)} />
              <div>
                <p className="text-[13px] tracking-[0.24em] uppercase text-black/50">{serieLabel}</p>
                <h1 className="mt-2 text-3xl sm:text-4xl tracking-[0.16em] uppercase text-black">{REF}</h1>
                <p className="mt-2 text-black/50">{t.tile(p.pips)}</p>
              </div>
            </div>

            <p className="mt-10 text-[13px] tracking-[0.2em] uppercase text-black/50">{t.format}</p>

            <p className="mt-8 text-[22px] leading-[1.6] text-black/80">{ambiance}</p>
            <p className="mt-8 text-black/50">{t.perfumer}</p>

            {/* Renvoi vers la même tuile dans l'autre série */}
            <Link to={lp(lang, `/domino/serie-${other}/${roman}`)} data-testid="mirror-link"
              className="mt-10 inline-block border-t border-black/10 pt-6 val-link text-[15px]">
              {t.mirror}
            </Link>

            <div className="mt-10 border-t border-black/10 pt-10">
              {p.available ? (
                <div data-testid="product-available">
                  <p className="text-3xl text-black">{p.price} €</p>
                  <p className="mt-2 text-[13px] tracking-[0.16em] uppercase text-black/45">{t.rrp}</p>
                  <Link to={lp(lang, "/contact")} className="val-btn mt-8" data-testid="product-enquire">{t.enquire}</Link>
                </div>
              ) : (
                <div data-testid="product-sold-out">
                  <p className="text-[13px] tracking-[0.2em] uppercase text-black/50">{t.soldOut}</p>
                  <NotifyForm productRef={REF + " — " + serieLabel} t={t} />
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
